import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BarcodeScannerProps {
  onScan: (code: string) => void;
  isScanning: boolean;
  setIsScanning: (v: boolean) => void;
}

const BarcodeScanner = ({ onScan, isScanning, setIsScanning }: BarcodeScannerProps) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string>("");

  const startScanner = async () => {
    setError("");
    try {
      const scanner = new Html5Qrcode("barcode-reader");
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 280, height: 150 },
          aspectRatio: 1.777,
        },
        (decodedText) => {
          onScan(decodedText);
          stopScanner();
        },
        () => {}
      );
      setIsScanning(true);
    } catch (err) {
      console.error("Scanner error:", err);
      setError("Camera access denied. Please allow camera permissions or enter UPC manually.");
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (e) {
        // ignore
      }
      scannerRef.current = null;
    }
    setIsScanning(false);
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="relative rounded-lg overflow-hidden border-2 border-primary/40 glow-green">
        <div
          id="barcode-reader"
          className="w-full bg-muted"
          style={{ minHeight: isScanning ? 260 : 180 }}
        />
        {!isScanning && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-card/90 backdrop-blur-sm">
            <Camera className="w-12 h-12 text-primary mb-3 animate-pulse-glow" />
            <p className="text-sm text-muted-foreground font-mono mb-4">
              SCANNER READY
            </p>
            <Button
              onClick={startScanner}
              className="bg-primary text-primary-foreground font-mono font-bold tracking-wider hover:glow-green-strong"
            >
              START SCAN
            </Button>
          </div>
        )}
        {isScanning && (
          <div className="absolute top-2 right-2 z-10">
            <Button
              size="sm"
              variant="outline"
              onClick={stopScanner}
              className="border-primary/40 text-primary font-mono text-xs"
            >
              <CameraOff className="w-3 h-3 mr-1" />
              STOP
            </Button>
          </div>
        )}
        {isScanning && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-primary/60 animate-pulse-glow" />
          </div>
        )}
      </div>
      {error && (
        <p className="text-destructive text-sm font-mono mt-2 text-center">{error}</p>
      )}
    </div>
  );
};

export default BarcodeScanner;
